import {useMemo} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {SalesCompare} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {ColumnFlexBox, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {
  GrayCenterBox, HighlightBox, SectionBlock, SectionSubTitle, SectionTitle
} from '../../StyledComponents';
import {SalesDayTimeSynchronizedAreaChart} from '../Chart/SalesDayTimeSynchronizedAreaChart';
import {EMPTY_ITEM_TYPES, SALES_WEEK_WEEKEND_LABELS} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {SalesDataProps} from '../interface';

export const SalesWeekWeekendCompare = ({
  isPending,
  error,
  data: rawdata,
}: SalesDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'kodata' && source.data.indexOf('매출 정보') !== -1
      ),
    [dataSource]
  );

  const data = useMemo(() => {
    return rawdata?.map((i) => i.dayOfWeek);
  }, [rawdata]);

  return (
    <SectionBlock>
      <SectionTitle>결제 비중(요일)</SectionTitle>
      <br />
      <SectionSubTitle>{`${metadata?.source || '카드사'}∙${
        metadata?.baseDate || ''
      }`}</SectionSubTitle>
      <PaddingBox top={16}>
        {isPending ? (
          <GrayCenterBox>
            <LoadingBar color={palette.textSecondary} />
          </GrayCenterBox>
        ) : error ? (
          <Typography variant="medium12" color={palette.textSecondary}>
            분석 실패
          </Typography>
        ) : !data ? (
          <EmptyItem type={EMPTY_ITEM_TYPES.Sales} />
        ) : (
          <SalesWeekWeekendChartBox items={data} />
        )}
      </PaddingBox>
    </SectionBlock>
  );
};

const SalesWeekWeekendChartBox = ({
  items,
}: {
  items: Array<SalesCompare['dayOfWeek']>;
}) => {
  const compareList = items.map((_, idx) => `비교 상권 ${idx + 1}`);

  const maxData = items.map((item) => {
    const sortedData = item
      .map((value, index) => ({value: Math.round(value * 10) / 10 || 0, index}))
      .sort((a, b) => b.value - a.value);
    const maxValues = sortedData.filter(
      (data, idx, arr) => idx < 3 && data.value === arr[0].value
    );

    return {
      maxValues: maxValues.map((data) => ({
        value: data.value,
        index: data.index,
      })),
    };
  });

  const makeMaxPerIndex = (index: number) => {
    return maxData[index].maxValues;
  };

  return (
    <ColumnFlexBox gap={16}>
      {items.map((item, index) => {
        return (
          <SalesDayTimeSynchronizedAreaChart
            key={`결제비중요일-${index}`}
            type={'결제비중요일'}
            data={item}
            compareItemName={compareList[index]}
            compareItemIndex={index}
            labels={SALES_WEEK_WEEKEND_LABELS}
          />
        );
      })}
      <HighlightBox>
        {!!compareList.length && (
          <>
            {compareList.map((item, index) => {
              const maxPerIndex = makeMaxPerIndex(index);

              return (
                <Typography
                  variant="medium12"
                  as="div"
                  key={`SalesWeekWeekendCompare-CompareList-${index}`}
                >
                  <Typography color={palette.primary500} fontWeight="700">
                    {item}
                  </Typography>
                  은/는{' '}
                  {!!maxPerIndex?.length ? (
                    <>
                      <strong>{`${maxData[index].maxValues
                        .map(
                          (data) =>
                            `${SALES_WEEK_WEEKEND_LABELS[data.index]}요일`
                        )
                        .join(', ')}`}</strong>
                      의 결제 비중이 가장 높아요.
                    </>
                  ) : (
                    '정보가 없어요.'
                  )}
                </Typography>
              );
            })}
          </>
        )}
      </HighlightBox>
    </ColumnFlexBox>
  );
};
